import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["display", "input"]
  static values = { 
    url: String,
    field: String,
    model: String,
    id: Number
  }

  connect() {
    this.isEditing = false
  }

  edit() {
    if (this.isEditing) return
    
    this.isEditing = true
    this.displayTarget.style.display = 'none'
    this.inputTarget.style.display = 'block'
    this.inputTarget.focus()
    
    // Select all text for text inputs, but not for textareas
    if (this.inputTarget.tagName === 'INPUT') {
      this.inputTarget.select()
    }
  }

  save() {
    if (!this.isEditing) return
    
    const value = this.inputTarget.value.trim()
    // Get the original value without HTML tags for comparison
    const originalValue = this.fieldValue === 'title' 
      ? this.displayTarget.querySelector('strong')?.textContent.trim() || ''
      : this.displayTarget.textContent.trim()
    
    if (value === originalValue) {
      this.cancel()
      return
    }

    // Validate required fields
    if (this.fieldValue === 'title' && !value) {
      this.inputTarget.classList.add('is-invalid')
      return
    }

    this.isEditing = false
    this.displayTarget.style.display = 'block'
    this.inputTarget.style.display = 'none'
    this.inputTarget.classList.remove('is-invalid')

    // Store original value for error handling
    this.originalValue = originalValue

    // Show loading state
    this.displayTarget.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Saving...'

    // Make AJAX request to update the field
    const url = this.urlValue.endsWith('.json') ? this.urlValue : this.urlValue + '.json'
    fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
      },
      body: JSON.stringify({
        [this.modelValue]: {
          [this.fieldValue]: value
        }
      })
    })
    .then(response => {
      if (response.ok) {
        return response.json()
      } else {
        throw new Error('Update failed')
      }
    })
    .then(data => {
      // Update the display with the new value, preserving HTML structure for title
      if (this.fieldValue === 'title') {
        this.displayTarget.innerHTML = `<strong>${value}</strong><i class="fas fa-edit text-muted ml-1" style="font-size: 0.8em;"></i>`
      } else {
        this.displayTarget.textContent = value.length > 100 ? value.substring(0, 100) + '...' : value
      }
      this.displayTarget.classList.add('text-success')
      setTimeout(() => {
        this.displayTarget.classList.remove('text-success')
      }, 2000)
    })
    .catch(error => {
      console.error('Error updating field:', error)
      // Restore original content, preserving HTML structure for title
      if (this.fieldValue === 'title') {
        this.displayTarget.innerHTML = `<strong>${this.originalValue}</strong><i class="fas fa-edit text-muted ml-1" style="font-size: 0.8em;"></i>`
      } else {
        this.displayTarget.textContent = this.originalValue
      }
      this.displayTarget.classList.add('text-danger')
      setTimeout(() => {
        this.displayTarget.classList.remove('text-danger')
      }, 2000)
    })
  }

  cancel() {
    if (!this.isEditing) return
    
    this.isEditing = false
    // Get the original value without HTML tags
    const originalValue = this.fieldValue === 'title' 
      ? this.displayTarget.querySelector('strong')?.textContent.trim() || ''
      : this.displayTarget.textContent.trim()
    this.inputTarget.value = originalValue
    this.displayTarget.style.display = 'block'
    this.inputTarget.style.display = 'none'
  }

  handleKeydown(event) {
    if (event.key === 'Enter') {
      event.preventDefault()
      this.save()
    } else if (event.key === 'Escape') {
      event.preventDefault()
      this.cancel()
    }
  }
} 